/** @jsxImportSource solid-js */

import { Component, Show, createSignal, createEffect } from 'solid-js';
import { translate } from '@fulcrum/lib';
import {
  IAttachedFileList,
  ITab,
  IAttachAsUrlProps,
  IAttachUrlFormData,
} from './types';

interface IAttachAsUrlFormProps {
  readonly formData: IAttachUrlFormData;
  onCreateSubmit(values: IAttachUrlFormData): void;
  addRef?(el: HTMLButtonElement): void;
}

const AttachAsUrlForm: Component<IAttachAsUrlFormProps> = (props) => {
  const [displayText, setDisplayText] = createSignal('');
  const [webUrl, setWebUrl] = createSignal('');

  // Sync signals with props in a tracked scope
  createEffect(() => {
    setDisplayText(props.formData.displayText || '');
    setWebUrl(props.formData.webUrl || '');
  });
  const [displayTextError, setDisplayTextError] = createSignal('');
  const [webUrlError, setWebUrlError] = createSignal('');

  const validateDisplayText = (value: string): boolean => {
    if (!value.trim()) {
      setDisplayTextError(translate('TC.Common.FieldRequired'));
      return false;
    }
    const maxLength = 255;
    if (value.length > maxLength) {
      setDisplayTextError(translate('NAME_TOO_LONG'));
      return false;
    }
    setDisplayTextError('');
    return true;
  };

  const validateWebUrl = (value: string): boolean => {
    if (!value.trim()) {
      setWebUrlError(translate('RequiredError'));
      return false;
    }

    const urlPattern =
      /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%\-/]))?/;
    if (!urlPattern.test(value)) {
      setWebUrlError(translate('TC.Common.InvalidURL'));
      return false;
    }

    setWebUrlError('');
    return true;
  };

  const handleSubmit = (e: Event): void => {
    e.preventDefault();
    const isDisplayTextValid = validateDisplayText(displayText());
    const isWebUrlValid = validateWebUrl(webUrl());

    if (isDisplayTextValid && isWebUrlValid) {
      props.onCreateSubmit({
        displayText: displayText(),
        webUrl: webUrl(),
      });
    }
  };

  const handleDisplayTextInput = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    setDisplayText(target.value);
    validateDisplayText(target.value);
  };

  const handleWebUrlInput = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    setWebUrl(target.value);
    validateWebUrl(target.value);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div class="sub-section">
        <div class="section-details">
          <div class="form-group">
            <label for="displayText">
              {translate('TC.Attachments.DisplayText')}
            </label>
            <input
              id="displayText"
              name="displayText"
              type="text"
              class={`form-control ${displayTextError() ? 'error' : ''}`}
              value={displayText()}
              onInput={handleDisplayTextInput}
              autocomplete="off"
            />
            <Show when={displayTextError()}>
              <div class="error-message">{displayTextError()}</div>
            </Show>
          </div>
        </div>
      </div>
      <div class="sub-section">
        <div class="section-details">
          <div class="form-group">
            <label for="webUrl">{translate('TC.Attachments.WebURL')}</label>
            <input
              id="webUrl"
              name="webUrl"
              type="text"
              class={`form-control ${webUrlError() ? 'error' : ''}`}
              value={webUrl()}
              onInput={handleWebUrlInput}
              autocomplete="off"
            />
            <Show when={webUrlError()}>
              <div class="error-message">{webUrlError()}</div>
            </Show>
          </div>
        </div>
      </div>
      <button class="d-none" type="submit" ref={props.addRef} />
    </form>
  );
};

const AttachAsUrlContainer: Component<IAttachAsUrlProps> = (props) => {
  const [formData] = createSignal<IAttachUrlFormData>({
    displayText: '',
    webUrl: '',
  });

  const onCreateSubmit = (values: IAttachUrlFormData): void => {
    const file: IAttachedFileList = {
      selectedObject: { displayText: values.displayText, url: values.webUrl },
      type: ITab.Link,
      source: 'attachment-picker',
    };

    props.onFilesSubmit(file);
    props.addAttachment(file);
  };

  return (
    <Show when={props.tab === ITab.Link && props.isShowLink}>
      <AttachAsUrlForm
        formData={formData()}
        onCreateSubmit={onCreateSubmit}
        addRef={props.addRef}
      />
    </Show>
  );
};

export default AttachAsUrlContainer;
